import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const WASTE_TYPES = [
  { icon: 'ri-seedling-line', label: 'Puit' },
  { icon: 'ri-drop-line', label: 'Kile' },
  { icon: 'ri-box-3-line', label: 'Kartong' },
  { icon: 'ri-recycle-line', label: 'Plast' },
  { icon: 'ri-tools-line', label: 'Metall' },
  { icon: 'ri-plug-line', label: 'Juhtmed' },
  { icon: 'ri-building-2-line', label: 'Kips' },
  { icon: 'ri-bubble-chart-line', label: 'EPS (penoplast)' },
  { icon: 'ri-cloud-line', label: 'Mineraalvill' },
  { icon: 'ri-alarm-warning-line', label: 'Ohtlikud jäätmed' },
  { icon: 'ri-file-damage-line', label: 'SPS' },
];

const SECONDARY_SERVICES = [
  {
    id: 'vahekoristus',
    icon: 'ri-broom-line',
    title: 'Ehitusaegne vahekoristus',
    description:
      'Hoiame objekti korras kogu ehitusprotsessi vältel, eemaldades jooksvalt jäätmed ja tagades ohutu töökeskkonna.',
    color: 'bg-pale-green',
    iconColor: 'text-eco-green',
    iconBg: 'bg-white',
    imageUrl:
      'https://public.readdy.ai/ai/img_res/6a8a30280cf81930b78dfa3fc73890b4.jpg',
  },
  {
    id: 'abitoolised',
    icon: 'ri-team-line',
    title: 'Abitööliste rent',
    description:
      'Pakume kogenud abitöölisi koos tööriistadega. Lisaks: ajutiste piirete ehitus, treppide katmine ja avade sulgemine.',
    color: 'bg-white',
    iconColor: 'text-dark-green',
    iconBg: 'bg-pale-green',
    imageUrl: null,
  },
  {
    id: 'loppkoristus',
    icon: 'ri-home-smile-line',
    title: 'Lõppkoristus',
    description:
      'Teostame põhjaliku ehitusjärgse koristuse, et objekt oleks valmis üleandmiseks.',
    color: 'bg-light-green',
    iconColor: 'text-eco-green',
    iconBg: 'bg-white',
    imageUrl: null,
  },
];

const EXTRA_SERVICES = [
  {
    icon: 'ri-truck-2-line',
    title: 'Transport ja logistika',
    desc: 'Korraldame materjali ja seadmete transpordi objektile ja objektilt vastavalt vajadusele.',
    imageUrl:
      'https://readdy.ai/api/search-image?query=professional%20heavy%20duty%20construction%20logistics%20truck%20loaded%20with%20building%20materials%20driving%20to%20construction%20site%20wide%20angle%20view%20clear%20sky%20organized%20industrial%20transport%20fleet%20green%20eco-friendly%20modern%20vehicles%20professional%20freight%20service&width=800&height=420&seq=iisral-transport-card-v2&orientation=landscape',
  },
  {
    icon: 'ri-layout-masonry-line',
    title: 'Ajutised piirded ja kattematerjalid',
    desc: 'Paigaldame ajutised kaitsepiiarded, katmeid ja muid abivahendeid ehituse ajal.',
    imageUrl: null,
  },
  {
    icon: 'ri-tools-fill',
    title: 'Objekti ettevalmistus',
    desc: 'Valmistame objekti ette tööde alustamiseks — puhastus, planeerimine ja korraldus.',
    imageUrl: null,
  },
];

export default function Services() {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();
  const { ref: mainRef, isVisible: mainVisible } = useScrollAnimation();
  const { ref: secRef, isVisible: secVisible } = useScrollAnimation();
  const { ref: extraRef, isVisible: extraVisible } = useScrollAnimation();

  return (
    <section id="teenused" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-8">

        {/* Header */}
        <div
          ref={headRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-flex items-center gap-2 text-eco-green text-xs md:text-[42px] font-bold uppercase tracking-[0.2em] mb-4">
            <div className="w-6 h-0.5 bg-eco-green" />
            Mida me pakume
            <div className="w-6 h-0.5 bg-eco-green" />
          </span>
        </div>

        {/* Main service card */}
        <div
          ref={mainRef}
          className={`mb-6 transition-all duration-700 ${
            mainVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="bg-dark-green rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left: Image */}
              <div className="w-full h-64 lg:h-auto min-h-[320px]">
                <img
                  src="https://readdy.ai/api/search-image?query=covered%20indoor%20construction%20waste%20sorting%20recycling%20facility%20multiple%20labeled%20containers%20bins%20separated%20wood%20planks%20plastic%20sheets%20cardboard%20metal%20pieces%20cables%20professional%20industrial%20waste%20management%20setup%20clean%20organized%20environment%20bright%20lighting%20green%20sustainability%20concept%20professional&width=800&height=500&seq=iisral-services-main-001&orientation=landscape"
                  alt="Ehitusjäätmete käitlemine objektil"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Right: Content */}
              <div className="p-10 flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 bg-eco-green/20 text-eco-green rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider mb-5">
                    <i className="ri-star-line text-xs" />
                    Peamine teenus
                  </div>
                  <h3 className="text-2xl font-extrabold text-white mb-3">
                    Ehitusjäätmete käitlemine objektil
                  </h3>
                  <p className="text-white/65 text-sm leading-relaxed mb-6">
                    Sorteerime ja käitleme ehitusjäätmeid otse objektil spetsiaalselt loodud alal
                    või kaetud telgis, mis on kaitstud ilmastiku eest.
                  </p>

                  {/* Waste types */}
                  <div className="mb-6">
                    <p className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-3">
                      Eraldame:
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {WASTE_TYPES.map((w) => (
                        <div key={w.label} className="flex items-center gap-2">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-eco-green/15 text-eco-green flex-shrink-0">
                            <i className={`${w.icon} text-xs`} />
                          </div>
                          <span className="text-white/75 text-xs font-medium">{w.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <p className="text-eco-green text-sm font-semibold">
                    Eesmärk: maksimaalne taaskasutus ja korrektne käitlemine
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary services */}
        <div
          ref={secRef}
          className={`grid grid-cols-1 md:grid-cols-3 gap-5 transition-all duration-700 ${
            secVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {SECONDARY_SERVICES.map((svc, i) => (
            <div
              key={svc.id}
              className={`${svc.color} rounded-2xl overflow-hidden flex flex-col border border-gray-100 transition-all duration-300`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {svc.imageUrl && (
                <div className="w-full h-44 flex-shrink-0">
                  <img
                    src={svc.imageUrl}
                    alt={svc.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              )}
              <div className="p-8 flex flex-col gap-5 flex-1">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${svc.iconBg}`}>
                  <i className={`${svc.icon} text-2xl ${svc.iconColor}`} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{svc.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{svc.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Lisateenused ── */}
        <div
          ref={extraRef}
          className={`mt-16 transition-all duration-700 ${extraVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-5 h-0.5 bg-eco-green" />
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-eco-green">Lisateenused</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {EXTRA_SERVICES.map((svc, i) => (
              <div
                key={svc.title}
                className="bg-[#F7F9F7] rounded-2xl overflow-hidden flex flex-col border border-gray-100"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                {svc.imageUrl && (
                  <div className="w-full h-40 flex-shrink-0">
                    <img
                      src={svc.imageUrl}
                      alt={svc.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                )}
                <div className="p-7 flex flex-col gap-4 flex-1">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white text-eco-green">
                    <i className={`${svc.icon} text-xl`} />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">{svc.title}</h4>
                    <p className="text-gray-400 text-xs leading-relaxed">{svc.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
